import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, Minus, Brain, Hammer, HeartHandshake } from "lucide-react";
import { useFetch, selectCls, tightDomain } from "../../lib/hooks";

const META = {
  smart: { icon: Brain, color: "#0A5C99", bg: "bg-sky-50 dark:bg-sky-950/40" },
  skill: { icon: Hammer, color: "#F97316", bg: "bg-orange-50 dark:bg-orange-950/40" },
  attitude: { icon: HeartHandshake, color: "#10B981", bg: "bg-emerald-50 dark:bg-emerald-950/40" },
};

function TrackCard({ k, track, series }) {
  const M = META[k];
  const Arrow = track.trend === "up" ? TrendingUp : track.trend === "down" ? TrendingDown : Minus;
  const tone = track.trend === "up" ? "text-emerald-600" : track.trend === "down" ? "text-red-600" : "text-slate-500";
  return (
    <div className={`rounded-xl border border-slate-200 dark:border-slate-700 p-4 ${M.bg}`} data-testid={`growth-track-${k}`}>
      <div className="flex items-center gap-2 mb-1"><M.icon className="h-5 w-5" style={{ color: M.color }} /><h4 className="font-heading font-bold text-sm">{track.label}</h4></div>
      <div className="flex items-end gap-2">
        <span className="font-mono text-3xl font-bold" data-testid={`growth-last-${k}`}>{track.last ?? "–"}</span>
        <span className={`inline-flex items-center gap-1 pb-1.5 text-sm font-semibold ${tone}`} data-testid={`growth-delta-${k}`}><Arrow className="h-4 w-4" />{track.delta == null ? "" : `${track.delta > 0 ? "+" : ""}${track.delta}`}</span>
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5" data-testid={`growth-sentence-${k}`}>{track.sentence}</p>
      <div className="h-24 mt-2">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 300, height: 96 }}>
          <LineChart data={series} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" /><YAxis domain={tightDomain(series, [k])} tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => [v, track.label]} />
            <Line type="monotone" dataKey={k} stroke={M.color} strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function GrowthCharts({ studentId, groups, title = "Tumbuh Seiring Waktu" }) {
  const [gid, setGid] = useState("");
  const q = studentId ? `student_id=${studentId}` : gid ? `group_id=${gid}` : "";
  const { data } = useFetch(`/growth${q ? `?${q}` : ""}`);
  return (
    <div className="surface p-5" data-testid="growth-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[200px]"><h3 className="font-heading font-bold">{title}</h3><p className="text-xs text-slate-500">Apakah {studentId ? "siswa ini" : "siswa"} makin pintar, makin terampil, dan makin baik sikapnya? Rata-rata per minggu (0–100).</p></div>
        {!studentId && groups?.length > 0 && <select data-testid="growth-group-select" className={`${selectCls} !w-auto`} value={gid} onChange={(e) => setGid(e.target.value)}><option value="">Semua rombel</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>}
      </div>
      {data && data.series.length === 0 && <p className="text-sm text-slate-400" data-testid="growth-empty">Belum ada nilai yang masuk. Begitu guru mulai mengisi penilaian harian, grafik pertumbuhan muncul di sini.</p>}
      {data && data.series.length > 0 && <div className="grid gap-4 md:grid-cols-3">{Object.entries(data.tracks).map(([k, t]) => <TrackCard key={k} k={k} track={t} series={data.series} />)}</div>}
    </div>
  );
}
